import { useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  SquaresFour,
  Buildings,
  ClipboardText,
  GearSix,
  UsersThree,
  Scales,
  Users,
  CurrencyGbp,
  Headset,
  Bell,
  UserCirclePlus,
  Storefront,
  Tag,
} from '@phosphor-icons/react';
import { usePlatformAuthStore } from '@/features/platform-auth/store';
import { useQueryClient } from '@tanstack/react-query';
import { usePlatformWebSocketStore, onPlatformSupportUpdate } from '@/lib/platformWebsocket';
import { usePlatformThemeStore } from '@/lib/platformThemeStore';
import { platformApi } from '@/lib/platformApi';
import { PlatformAccountMenu } from './PlatformAccountMenu';
import { toast } from 'sonner';

const NAV_SECTIONS = [
  {
    label: undefined,
    items: [
      { label: 'Dashboard', icon: SquaresFour, to: '/platform' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { label: 'All Customers', icon: Storefront, to: '/platform/customers' },
      { label: 'Onboarding', icon: UserCirclePlus, to: '/platform/onboarding' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Tenants', icon: Buildings, to: '/platform/tenants' },
      { label: 'Users', icon: Users, to: '/platform/users' },
      { label: 'Support', icon: Headset, to: '/platform/support' },
      // Security and Audit Log were two views over two different tables. Activity Logs is one
      // destination over three (audit_logs + platform_audit_log + permission_audit_logs). The
      // old routes still resolve — they redirect here with an equivalent filter preset.
      { label: 'Activity Logs', icon: ClipboardText, to: '/platform/activity' },
      { label: 'Notifications', icon: Bell, to: '/platform/notifications' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Plans & Pricing', icon: Tag, to: '/platform/plans-pricing' },
      { label: 'Billing', icon: CurrencyGbp, to: '/platform/billing' },
    ],
  },
  {
    label: 'Account',
    items: [
      // Who can sign in here and receive an assigned ticket — see TeamPage.
      { label: 'Support Executives', icon: UsersThree, to: '/platform/team' },
      { label: 'Settings', icon: GearSix, to: '/platform/settings' },
    ],
  },
];

/** Derive a page title from the current pathname */
function getPageTitle(pathname: string): string {
  if (pathname === '/platform') return 'Platform Overview';
  if (pathname === '/platform/tenants') return 'Tenants';
  if (pathname.startsWith('/platform/tenants/')) return 'Tenant Detail';
  if (pathname === '/platform/users') return 'Users';
  if (pathname.startsWith('/platform/users/')) return 'User Detail';
  if (pathname === '/platform/plans-pricing') return 'Plans & Pricing';
  if (pathname === '/platform/billing') return 'Billing & Subscriptions';
  if (pathname === '/platform/support') return 'Support Tickets';
  if (pathname.startsWith('/platform/support/')) return 'Support Ticket';
  if (pathname === '/platform/activity') return 'Activity Logs';
  if (pathname === '/platform/security') return 'Security';
  if (pathname === '/platform/notifications') return 'Notification Health';
  if (pathname === '/platform/audit') return 'Audit Log';
  if (pathname === '/platform/customers') return 'Customers';
  if (pathname === '/platform/onboarding') return 'Onboarding Sessions';
  if (pathname.startsWith('/platform/settings')) return 'Settings';
  return 'Platform Console';
}

export function PlatformShell() {
  // Re-read the preference for whoever is signed in NOW. Without this, admin B inherits the
  // theme admin A was last using on this browser until they touch the toggle themselves.
  const adminId = usePlatformAuthStore((s) => s.admin?.id);

  // ── The console's live channel ────────────────────────────────────────────────────────────
  // The shell owns it, and only the shell. Every Support surface (the queue, the detail page,
  // the conversation drawer) reads React Query, so ONE socket here refreshes all of them —
  // whereas a socket per SupportConversation would open and close a connection every time a
  // drawer is toggled, and would authenticate again on each navigation.
  const platformToken = usePlatformAuthStore((s) => s.accessToken);
  const wsConnect = usePlatformWebSocketStore((s) => s.connect);
  const wsDisconnect = usePlatformWebSocketStore((s) => s.disconnect);
  const wsConnected = usePlatformWebSocketStore((s) => s.connected);
  const queryClient = useQueryClient();
  const prevWsConnectedRef = useRef(wsConnected);

  // Connect once the in-memory access token exists (it is null until the first /auth/refresh),
  // reconnect when it is replaced so the server validates the CURRENT token, and disconnect on
  // sign-out or unmount — leaving no authenticated socket behind a signed-out console.
  useEffect(() => {
    if (platformToken) wsConnect();
    else wsDisconnect();
    return () => wsDisconnect();
  }, [platformToken, wsConnect, wsDisconnect]);

  // Support updates: refresh the server-derived queue and thread. Identifier in, HTTP refetch
  // out — nothing about the ticket is taken from the socket payload. This never marks anything
  // read: the unanswered badge is derived server-side and must stay that way.
  useEffect(() => {
    // `wsConnected` is a dependency because the subscription binds to the socket instance the
    // store holds right now, and a token change replaces that instance.
    const unsub = onPlatformSupportUpdate((event) => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
      queryClient.invalidateQueries({ queryKey: ['platform', 'support', event.ticketId] });
    });
    return () => unsub();
  }, [wsConnected, queryClient]);

  // Socket.io replays nothing that was missed while the socket was down, so recover the queue
  // on every (re)connect.
  useEffect(() => {
    if (!prevWsConnectedRef.current && wsConnected) {
      queryClient.invalidateQueries({ queryKey: ['platform', 'support'] });
    }
    prevWsConnectedRef.current = wsConnected;
  }, [wsConnected, queryClient]);
  const syncForAdmin = usePlatformThemeStore((s) => s.syncForAdmin);
  useEffect(() => { syncForAdmin(adminId); }, [adminId, syncForAdmin]);

  const admin = usePlatformAuthStore((s) => s.admin);
  const clear = usePlatformAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  async function handleLogout() {
    try {
      await platformApi.post('/auth/logout', {});
    } catch {
      // Ignore — clear session regardless
    }
    clear();
    toast.success('Signed out of Platform Console');
    navigate('/platform/login', { replace: true });
  }

  const adminName = admin ? `${admin.firstName} ${admin.lastName}` : '';

  return (
    <div className="flex min-h-screen bg-plat-bg">
      {/* ── Sidebar ────────────────────────────────────────────── */}
      <aside className="sticky top-0 flex h-screen w-[240px] shrink-0 flex-col border-r border-plat-sidebar-border/50 bg-plat-sidebar-bg">
        {/* Brand */}
        <div className="flex h-16 items-center border-b border-plat-sidebar-border/50 px-5">
          <div className="flex items-center gap-3">
            <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-plat-accent-solid to-plat-accent-solid-hover text-plat-on-accent shadow-lg shadow-plat-accent-solid/30">
              <Scales className="h-[18px] w-[18px]" weight="bold" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-[15px] font-bold leading-tight tracking-tight text-plat-sidebar-fg">
                Platform Console
              </div>
              <div className="mt-0.5 truncate text-[10px] font-medium uppercase tracking-[0.1em] text-plat-sidebar-muted">
                Super Admin
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-5">
          {NAV_SECTIONS.map((section, i) => (
            <div key={i} className="mb-6">
              {section.label && (
                <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.1em] text-plat-sidebar-muted">
                  {section.label}
                </div>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/platform'}
                      className={({ isActive }) =>
                        [
                          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
                          isActive
                            ? 'bg-plat-sidebar-selected/15 text-plat-sidebar-fg shadow-sm'
                            : 'text-plat-sidebar-muted hover:bg-plat-sidebar-hover/70 hover:text-plat-sidebar-secondary',
                        ].join(' ')
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-plat-sidebar-accent" />
                          )}
                          <span className={[
                            'shrink-0 transition-colors duration-200',
                            isActive ? 'text-plat-sidebar-accent' : 'text-plat-sidebar-faint group-hover:text-plat-sidebar-secondary',
                          ].join(' ')}>
                            <Icon className="h-[18px] w-[18px]" />
                          </span>
                          <span className="flex-1 truncate">{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer — IDENTITY ONLY.
            The theme selector moved to Settings → Appearance & Regional: a preference is not
            navigation, and a colour switch parked in the footer is a setting in the wrong room. */}
        <div className="border-t border-plat-sidebar-border/50 p-3">
          {/* Identity only. Sign out lives in the top-right account area of the header — one
              control, one place. It used to be duplicated here. */}
          <div className="rounded-lg bg-plat-sidebar-hover/70 px-3 py-2.5">
            <div className="text-xs font-semibold text-plat-sidebar-fg">{adminName}</div>
            <div className="truncate text-[10px] text-plat-sidebar-muted">{admin?.email}</div>
          </div>
        </div>
      </aside>

      {/* ── Main ───────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-plat-border bg-plat-surface px-6">
          <h1 className="text-[15px] font-semibold text-plat-fg">{pageTitle}</h1>
          <PlatformAccountMenu admin={admin} onSignOut={() => void handleLogout()} />
        </header>

        {/* Content */}
        <main className="flex-1 overflow-x-hidden px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
